/**
 * @file PlanRegistryToolbar.jsx
 * @module ui/src/components/admin/PlanRegistryToolbar
 * @description
 *   Filter, search, sort, and saved-view controls for the plan registry. Receives
 *   reference data (fiscal periods, roles, segments) as props from PlanRegistry (C-01)
 *   and emits filter change events via onFilterChange. Makes no direct API calls —
 *   all data fetching is owned by C-01 PlanRegistry.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S777
 *
 * @architecture
 *   Locked decisions : #1
 *   Temporal invariants : INV-3
 *   Design principles : DP-5, DP-14
 *
 * @domain-rules
 *   - All filter state changes are emitted to the parent PlanRegistry via onFilterChange.
 *   - Reference data (fiscal periods, roles, segments) is prop-supplied — never fetched here.
 *   - Saved view management UI is rendered here; saved view API calls are delegated to C-01.
 *   - Filter values map to query param names consumed by ep 1 GET /api/v1/plans/registry.
 *
 * @wiring-s777
 *   NO_API — prop-driven toolbar. No direct fetch() calls.
 *   ep 1 GET /api/v1/plans/registry is called by C-01; C-02 emits filter state only.
 *
 * @changelog
 *   1.0.0  S756  Initial build
 *   1.0.1  S777  Added JSDoc header. CRLF→LF.
 */

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * @typedef {{
 *   fiscalPeriodId: number | null,
 *   planStatus: string[],
 *   temporalRelationship: string[],
 *   roleIds: number[],
 *   segmentIds: number[],
 *   buIds: number[],
 *   primarySpine: string[],
 *   attentionLevel: string[],
 *   hasOpenAmendment: boolean | null,
 *   sourceType: string[],
 *   searchQuery: string,
 *   sortBy: string,
 *   sortDir: 'asc' | 'desc',
 * }} FilterState
 */

/**
 * @typedef {{
 *   id: string,
 *   label: string,
 *   filters: FilterState,
 * }} SavedView
 */

const QUICK_FILTERS = {
  needs_attention: {
    label: 'Needs Attention',
    updates: {
      attentionLevel: ['blocking', 'action_required'],
    },
  },
  drafts: {
    label: 'Drafts',
    updates: {
      planStatus: ['draft'],
    },
  },
  awaiting_approval: {
    label: 'Awaiting Approval',
    updates: {
      planStatus: ['pending_approval'],
    },
  },
  governing_now: {
    label: 'Governing Now',
    updates: {
      temporalRelationship: ['governs_current'],
    },
  },
  future_effective: {
    label: 'Future Effective',
    updates: {
      temporalRelationship: ['future_effective'],
    },
  },
  amendments: {
    label: 'Amendments',
    updates: {
      hasOpenAmendment: true,
    },
  },
  readiness_issues: {
    label: 'Readiness Issues',
    updates: {
      attentionLevel: ['monitoring'],
    },
  },
};

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'Last modified' },
  { value: 'plan_name', label: 'Plan name' },
  { value: 'effective_from', label: 'Effective date' },
  { value: 'role_name', label: 'Role' },
  { value: 'position_count', label: 'Position count' },
  { value: 'submission_age', label: 'Submission age' },
  { value: 'projected_payout', label: 'Projected payout' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const TEMPORAL_OPTIONS = [
  { value: 'governs_current', label: 'Governing now' },
  { value: 'governs_through', label: 'Governs through date' },
  { value: 'governs_from', label: 'Governs from date' },
  { value: 'future_effective', label: 'Future effective' },
  { value: 'historical', label: 'Historical' },
  { value: 'draft', label: 'Draft temporal state' },
];

function arraysEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  return [...a].sort().every((value, index) => {
    return value === [...b].sort()[index];
  });
}

function matchesQuickFilter(filters, updates) {
  return Object.entries(updates).every(([key, expected]) => {
    const actual = filters[key];

    if (Array.isArray(expected)) {
      return Array.isArray(actual) && arraysEqual(actual, expected);
    }

    return actual === expected;
  });
}

function filtersEquivalent(a, b) {
  if (!a || !b) return false;

  const arrayKeys = [
    'planStatus',
    'temporalRelationship',
    'roleIds',
    'segmentIds',
    'buIds',
    'primarySpine',
    'attentionLevel',
    'sourceType',
  ];

  for (const key of arrayKeys) {
    if (!arraysEqual(a[key] || [], b[key] || [])) return false;
  }

  return (
    (a.fiscalPeriodId ?? null) === (b.fiscalPeriodId ?? null) &&
    (a.hasOpenAmendment ?? null) ===
      (b.hasOpenAmendment ?? null) &&
    (a.searchQuery || '') === (b.searchQuery || '') &&
    (a.sortBy || 'updated_at') === (b.sortBy || 'updated_at') &&
    (a.sortDir || 'desc') === (b.sortDir || 'desc')
  );
}

function FilterDropdown({
  label,
  options,
  values,
  onChange,
  multiple = true,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (
        rootRef.current &&
        !rootRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const selectedValues = Array.isArray(values)
    ? values
    : values === null || values === undefined
      ? []
      : [values];

  const selectedLabels = options
    .filter((option) => selectedValues.includes(option.value))
    .map((option) => option.label);

  const buttonText =
    selectedLabels.length === 0
      ? label
      : selectedLabels.length === 1
        ? `${label}: ${selectedLabels[0]}`
        : `${label}: ${selectedLabels.length}`;

  function toggleValue(value) {
    if (!multiple) {
      onChange(value);
      setOpen(false);
      return;
    }

    if (selectedValues.includes(value)) {
      onChange(
        selectedValues.filter((candidate) => candidate !== value),
      );
    } else {
      onChange([...selectedValues, value]);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className="secondary-button whitespace-nowrap"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {buttonText} <span aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          className="absolute left-0 top-full z-30 mt-1 max-h-72 min-w-[210px] overflow-y-auto rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-1 shadow-lg"
        >
          {options.map((option) => {
            const selected = selectedValues.includes(option.value);

            return (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={selected}
                className={[
                  'flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-left text-[12px] text-[rgb(var(--color-text))]',
                  selected
                    ? 'bg-[rgb(var(--color-selected-bg))]'
                    : 'hover:bg-[rgb(var(--color-surface-muted))]',
                ].join(' ')}
                onClick={() => toggleValue(option.value)}
              >
                <span>{option.label}</span>
                {selected && (
                  <span aria-hidden="true">✓</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActiveFilterChip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-2 py-1 text-[10px] text-[rgb(var(--color-text))]">
      <span>{label}</span>
      <button
        type="button"
        className="rounded px-0.5 text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))]"
        aria-label={`Remove filter ${label}`}
        onClick={onRemove}
      >
        ×
      </button>
    </span>
  );
}

/**
 * Server-query controls for the plan registry.
 *
 * This component never filters plan records. It emits FilterState updates
 * to its parent, which is responsible for server requests.
 *
 * @param {{
 *   filters: FilterState,
 *   onFilterChange: (updates: Partial<FilterState>) => void,
 *   onClearFilters: () => void,
 *   savedViews: SavedView[],
 *   onSaveCurrentView: (label: string) => void,
 *   onSelectSavedView: (view: SavedView) => void,
 *   onDeleteSavedView: (viewId: string) => void,
 *   onNewPlan: () => void,
 *   fiscalPeriods: Array<{ id: number, label: string }>,
 *   roles: Array<{ id: number, name: string }>,
 *   segments: Array<{ id: number, name: string }>,
 *   loading?: boolean,
 *   className?: string
 * }} props
 */
export default function PlanRegistryToolbar({
  filters,
  onFilterChange,
  onClearFilters,
  savedViews,
  onSaveCurrentView,
  onSelectSavedView,
  onDeleteSavedView,
  onNewPlan,
  fiscalPeriods,
  roles,
  segments,
  loading = false,
  className = '',
}) {
  const [searchValue, setSearchValue] = useState(
    filters.searchQuery || '',
  );
  const [savedViewsOpen, setSavedViewsOpen] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [newViewLabel, setNewViewLabel] = useState('');

  const debounceRef = useRef(null);
  const savedViewsRef = useRef(null);

  useEffect(() => {
    setSearchValue(filters.searchQuery || '');
  }, [filters.searchQuery]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (
        savedViewsRef.current &&
        !savedViewsRef.current.contains(event.target)
      ) {
        setSavedViewsOpen(false);
        setSavingView(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setSavedViewsOpen(false);
        setSavingView(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick,
      );
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const fiscalPeriodOptions = useMemo(
    () =>
      fiscalPeriods.map((period) => ({
        value: period.id,
        label: period.label,
      })),
    [fiscalPeriods],
  );

  const roleOptions = useMemo(
    () =>
      roles.map((role) => ({
        value: role.id,
        label: role.name,
      })),
    [roles],
  );

  const segmentOptions = useMemo(
    () =>
      segments.map((segment) => ({
        value: segment.id,
        label: segment.name,
      })),
    [segments],
  );

  const roleMap = useMemo(
    () => new Map(roles.map((role) => [role.id, role.name])),
    [roles],
  );

  const segmentMap = useMemo(
    () =>
      new Map(
        segments.map((segment) => [segment.id, segment.name]),
      ),
    [segments],
  );

  const statusMap = useMemo(
    () =>
      new Map(
        STATUS_OPTIONS.map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [],
  );

  const temporalMap = useMemo(
    () =>
      new Map(
        TEMPORAL_OPTIONS.map((option) => [
          option.value,
          option.label,
        ]),
      ),
    [],
  );

  function handleSearchChange(value) {
    setSearchValue(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onFilterChange({ searchQuery: value });
    }, 300);
  }

  function clearSearch() {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    setSearchValue('');
    onFilterChange({ searchQuery: '' });
  }

  function toggleQuickFilter(key) {
    const quickFilter = QUICK_FILTERS[key];
    const active = matchesQuickFilter(
      filters,
      quickFilter.updates,
    );

    if (!active) {
      onFilterChange(quickFilter.updates);
      return;
    }

    const cleared = {};

    Object.entries(quickFilter.updates).forEach(
      ([field, value]) => {
        cleared[field] = Array.isArray(value) ? [] : null;
      },
    );

    onFilterChange(cleared);
  }

  function submitSavedView(event) {
    event.preventDefault();

    const label = newViewLabel.trim();
    if (!label) return;

    onSaveCurrentView(label);
    setNewViewLabel('');
    setSavingView(false);
    setSavedViewsOpen(false);
  }

  const activeFilterChips = [];

  filters.planStatus.forEach((value) => {
    activeFilterChips.push({
      key: `status-${value}`,
      label: `Status: ${statusMap.get(value) || value}`,
      remove: () =>
        onFilterChange({
          planStatus: filters.planStatus.filter(
            (item) => item !== value,
          ),
        }),
    });
  });

  filters.temporalRelationship.forEach((value) => {
    activeFilterChips.push({
      key: `temporal-${value}`,
      label: `Temporal: ${temporalMap.get(value) || value}`,
      remove: () =>
        onFilterChange({
          temporalRelationship:
            filters.temporalRelationship.filter(
              (item) => item !== value,
            ),
        }),
    });
  });

  filters.roleIds.forEach((id) => {
    activeFilterChips.push({
      key: `role-${id}`,
      label: `Role: ${roleMap.get(id) || id}`,
      remove: () =>
        onFilterChange({
          roleIds: filters.roleIds.filter(
            (item) => item !== id,
          ),
        }),
    });
  });

  filters.segmentIds.forEach((id) => {
    activeFilterChips.push({
      key: `segment-${id}`,
      label: `Segment: ${segmentMap.get(id) || id}`,
      remove: () =>
        onFilterChange({
          segmentIds: filters.segmentIds.filter(
            (item) => item !== id,
          ),
        }),
    });
  });

  filters.buIds.forEach((id) => {
    activeFilterChips.push({
      key: `bu-${id}`,
      label: `BU: ${id}`,
      remove: () =>
        onFilterChange({
          buIds: filters.buIds.filter(
            (item) => item !== id,
          ),
        }),
    });
  });

  filters.primarySpine.forEach((value) => {
    activeFilterChips.push({
      key: `spine-${value}`,
      label: `Spine: ${value}`,
      remove: () =>
        onFilterChange({
          primarySpine: filters.primarySpine.filter(
            (item) => item !== value,
          ),
        }),
    });
  });

  filters.attentionLevel.forEach((value) => {
    activeFilterChips.push({
      key: `attention-${value}`,
      label: `Attention: ${value.replaceAll('_', ' ')}`,
      remove: () =>
        onFilterChange({
          attentionLevel: filters.attentionLevel.filter(
            (item) => item !== value,
          ),
        }),
    });
  });

  filters.sourceType.forEach((value) => {
    activeFilterChips.push({
      key: `source-${value}`,
      label: `Source: ${value}`,
      remove: () =>
        onFilterChange({
          sourceType: filters.sourceType.filter(
            (item) => item !== value,
          ),
        }),
    });
  });

  if (filters.hasOpenAmendment !== null) {
    activeFilterChips.push({
      key: 'amendment',
      label: filters.hasOpenAmendment
        ? 'Has open amendment'
        : 'No open amendment',
      remove: () =>
        onFilterChange({ hasOpenAmendment: null }),
    });
  }

  if (filters.searchQuery) {
    activeFilterChips.push({
      key: 'search',
      label: `Search: ${filters.searchQuery}`,
      remove: clearSearch,
    });
  }

  const hasFilters = activeFilterChips.length > 0;

  return (
    <section
      className={`rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-4 ${className}`}
      aria-label="Plan registry controls"
    >
      <div className="flex items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <input
            type="search"
            role="searchbox"
            aria-label="Search plans"
            placeholder="Search by plan name, code, role, segment, rate table..."
            value={searchValue}
            disabled={loading}
            onChange={(event) =>
              handleSearchChange(event.target.value)
            }
            className="w-full rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg))] px-3 py-2 pr-9 text-[12px] text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-placeholder))]"
          />

          {searchValue && (
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded px-1 text-[rgb(var(--color-text-muted))]"
              aria-label="Clear plan search"
              onClick={clearSearch}
            >
              ×
            </button>
          )}
        </div>

        <div ref={savedViewsRef} className="relative">
          <button
            type="button"
            className="secondary-button whitespace-nowrap"
            aria-haspopup="menu"
            aria-expanded={savedViewsOpen}
            onClick={() =>
              setSavedViewsOpen((current) => !current)
            }
          >
            Saved Views ▾
          </button>

          {savedViewsOpen && (
            <div
              role="menu"
              aria-label="Saved plan views"
              className="absolute right-0 top-full z-40 mt-1 min-w-[280px] rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-1 shadow-lg"
            >
              {savedViews.length === 0 && !savingView && (
                <div className="px-3 py-2 text-[11px] text-[rgb(var(--color-text-muted))]">
                  No saved views yet
                </div>
              )}

              {savedViews.map((view) => {
                const applied = filtersEquivalent(
                  filters,
                  view.filters,
                );

                return (
                  <div
                    key={view.id}
                    className="group flex items-center gap-1 rounded hover:bg-[rgb(var(--color-surface-muted))]"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="flex min-w-0 flex-1 items-center gap-2 px-3 py-2 text-left text-[12px] text-[rgb(var(--color-text))]"
                      onClick={() => {
                        onSelectSavedView(view);
                        setSavedViewsOpen(false);
                      }}
                    >
                      <span
                        aria-hidden="true"
                        className="w-3 shrink-0"
                      >
                        {applied ? '✓' : ''}
                      </span>
                      <span className="truncate">
                        {view.label}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="mr-1 rounded px-2 py-1 text-[rgb(var(--color-text-muted))] opacity-0 hover:text-[rgb(var(--color-danger))] focus:opacity-100 group-hover:opacity-100"
                      aria-label={`Delete saved view ${view.label}`}
                      onClick={() => onDeleteSavedView(view.id)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}

              <div className="my-1 border-t border-[rgb(var(--color-border))]" />

              {!savingView ? (
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full rounded px-3 py-2 text-left text-[12px] text-[rgb(var(--color-primary))] hover:bg-[rgb(var(--color-surface-muted))]"
                  onClick={() => setSavingView(true)}
                >
                  Save current view
                </button>
              ) : (
                <form
                  onSubmit={submitSavedView}
                  className="p-2"
                >
                  <label
                    htmlFor="plan-view-name"
                    className="mb-1 block text-[10px] font-medium text-[rgb(var(--color-text-muted))]"
                  >
                    View name
                  </label>

                  <input
                    id="plan-view-name"
                    autoFocus
                    value={newViewLabel}
                    onChange={(event) =>
                      setNewViewLabel(event.target.value)
                    }
                    className="w-full rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg))] px-2 py-1.5 text-[12px] text-[rgb(var(--color-text))]"
                    maxLength={120}
                  />

                  <div className="mt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setSavingView(false);
                        setNewViewLabel('');
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="primary-button"
                      disabled={!newViewLabel.trim()}
                    >
                      Save
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          className="primary-button whitespace-nowrap"
          onClick={onNewPlan}
        >
          + New Plan
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <FilterDropdown
          label="Fiscal Period"
          options={fiscalPeriodOptions}
          values={filters.fiscalPeriodId}
          multiple={false}
          disabled={loading}
          onChange={(fiscalPeriodId) =>
            onFilterChange({ fiscalPeriodId })
          }
        />

        <FilterDropdown
          label="Status"
          options={STATUS_OPTIONS}
          values={filters.planStatus}
          disabled={loading}
          onChange={(planStatus) =>
            onFilterChange({ planStatus })
          }
        />

        <FilterDropdown
          label="Temporal"
          options={TEMPORAL_OPTIONS}
          values={filters.temporalRelationship}
          disabled={loading}
          onChange={(temporalRelationship) =>
            onFilterChange({ temporalRelationship })
          }
        />

        <FilterDropdown
          label="Role"
          options={roleOptions}
          values={filters.roleIds}
          disabled={loading}
          onChange={(roleIds) =>
            onFilterChange({ roleIds })
          }
        />

        <FilterDropdown
          label="Segment"
          options={segmentOptions}
          values={filters.segmentIds}
          disabled={loading}
          onChange={(segmentIds) =>
            onFilterChange({ segmentIds })
          }
        />

        <label className="flex items-center gap-2">
          <span className="sr-only">Sort plans by</span>
          <select
            value={filters.sortBy}
            disabled={loading}
            aria-label="Sort plans by"
            onChange={(event) =>
              onFilterChange({
                sortBy: event.target.value,
              })
            }
            className="rounded-md border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-[12px] text-[rgb(var(--color-text))]"
          >
            {SORT_OPTIONS.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                Sort: {option.label}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="icon-button"
          aria-label={
            filters.sortDir === 'asc'
              ? 'Sort descending'
              : 'Sort ascending'
          }
          onClick={() =>
            onFilterChange({
              sortDir:
                filters.sortDir === 'asc' ? 'desc' : 'asc',
            })
          }
        >
          <span aria-hidden="true">
            {filters.sortDir === 'asc' ? '↑' : '↓'}
          </span>
        </button>
      </div>

      <div
        className="mt-3 flex flex-wrap items-center gap-2"
        aria-label="Quick plan filters"
      >
        {Object.entries(QUICK_FILTERS).map(
          ([key, quickFilter]) => {
            const active = matchesQuickFilter(
              filters,
              quickFilter.updates,
            );

            return (
              <button
                key={key}
                type="button"
                role="checkbox"
                aria-checked={active}
                className={[
                  'rounded-full border px-3 py-1.5 text-[11px]',
                  active
                    ? 'border-[rgb(var(--color-primary))] bg-[rgb(var(--color-selected-bg))] text-[rgb(var(--color-primary))]'
                    : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text-muted))]',
                ].join(' ')}
                onClick={() => toggleQuickFilter(key)}
              >
                {quickFilter.label}
              </button>
            );
          },
        )}
      </div>

      {hasFilters && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-[rgb(var(--color-border))] pt-3">
          <span className="text-[10px] font-medium uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
            Active filters
          </span>

          {activeFilterChips.map((chip) => (
            <ActiveFilterChip
              key={chip.key}
              label={chip.label}
              onRemove={chip.remove}
            />
          ))}

          <button
            type="button"
            className="text-button ml-auto"
            aria-label="Clear all filters"
            onClick={onClearFilters}
          >
            Clear all
          </button>
        </div>
      )}
    </section>
  );
}